import { LAYERS } from '@/lib/constants/model'
import type { Material, Object3D, AnimationClip, Group } from 'three'
import { Mesh } from 'three'
import { sortBy } from 'ramda'
import type { ModelNode } from '@/entities/model3d'
import type { TextLabel } from '@/lib/three-d-resources/labels-manager'

type Layer = {
  title: string
  nodeId: number
  nodeName: string
  visible: boolean
  opacity: number
  canSetOpacity: boolean
  layerNumber: number
  color: string
  position?: number
  labels?: TextLabel[]
}

type DeprecatedModelType = {
  id: number
  title: string | null
  type: 'MOBILE' | 'DRACO' | 'GLTF'
  url: string
  layerList: ModelNode[]
  nodes: Map<number, ModelNode> // Keyed by node id
  sceneNodes: Map<number, Object3D> // Keyed by node id
  animations: Array<AnimationClip>
  nodeNameToId: Map<string, number>
  group: Group
  billboardLabels: Object3D[]
  meta: {
    hasLighting: boolean
  }
  updated_at: string
  text_scale: number
}

type LayerGroup = { name: string; layers: Layer[]; someVisible: boolean; allVisible: boolean; isDefault: boolean }

const _getMaterial = (sceneNode: Object3D | Mesh, nodes: Map<number, ModelNode>): Material | null => {
  const sortByLayerOrder = sortBy((node: Object3D) => nodes.get(node.userData.nodeId)?.layer ?? 1e10)
  let material = sceneNode instanceof Mesh ? sceneNode.material : null
  if (!material) {
    const children = sortByLayerOrder(sceneNode.children)
    // TODO: recurse down the node tree indefinitely as iOS does
    // So far, there doesn't seem to be a need for this, but there might be in the future.
    if (children[0] instanceof Mesh) {
      material = children[0].material
    }
  }

  return Array.isArray(material) ? material[0] : material
}

const _getLayerColour = (sceneNode: Object3D | Mesh | undefined, nodes: Map<number, ModelNode>): string => {
  const defaultColor = 'rgba(34, 34, 34, 1)'
  if (!sceneNode) {
    return defaultColor
  }

  let color = defaultColor
  // set the colors for the dot on the styling if there is a valid material
  //  and it can be read from the actual list.
  const material = _getMaterial(sceneNode, nodes)
  if (material) {
    if (!(material as any).color) {
      return color
    }
    let { r, g, b } = (material as any).color

    if (r !== 1 || g !== 1 || b !== 1) {
      // https://www.w3.org/TR/WCAG/#dfn-relative-luminance
      const rl = r <= 0.03928 ? r / 12.92 : Math.pow((r + 0.055) / 1.055, 2.4)
      const gl = g <= 0.03928 ? g / 12.92 : Math.pow((g + 0.055) / 1.055, 2.4)
      const bl = b <= 0.03928 ? b / 12.92 : Math.pow((b + 0.055) / 1.055, 2.4)
      const relativeLuminance = 0.2126 * rl + 0.7152 * gl + 0.0722 * bl

      // https://www.w3.org/TR/WCAG/#dfn-contrast-ratio
      // 1.05 because we assume the background is white
      const contrastRatio = 1.05 / (relativeLuminance + 0.05)

      // Invert the color if it's too close to white
      if (contrastRatio < 1.25) {
        r = 1 - r
        g = 1 - g
        b = 1 - b
      }

      color = `rgba(${Math.round(r * 255)}, ${Math.round(g * 255)}, ${Math.round(b * 255)}, 1)`
    }
  }

  return color
}

export const DEFAULT_CATEGORY = 'Other'

// A helper function for deriving this component's props from a Model.
// We can't pass the Model directly to the component's props because that involves wrapping it
// in a ref. Doing so attaches all the vue magic to the Model including the Three.js scene graph.
// This tanks the performance of ray casting (800ms vs 80ms)
export const modelToLayerGroups = (model: DeprecatedModelType): LayerGroup[] => {
  const categoryMap = model.layerList.reduce((catMap, layer) => {
    const category = layer.category ?? DEFAULT_CATEGORY
    if (!catMap[category]) {
      catMap[category] = {
        layers: [],
        allVisible: true,
        someVisible: false,
        isDefault: category === DEFAULT_CATEGORY,
      }
    }

    const sceneNode = model.sceneNodes.get(layer.id)

    // tree-traversed opacity uses Object3D.layers, quick algorithm uses Object3D.visible
    let visible = !!(sceneNode?.layers.isEnabled(LAYERS.DEFAULT) && sceneNode?.visible)

    // Use this layer if there is no parent category
    if (!layer.category || sceneNode instanceof Mesh) {
      let opacity = layer.opacity ?? 1
      let canSetOpacity = true
      if (sceneNode?.userData.originalOpacity && sceneNode?.userData.originalOpacity !== 1) {
        opacity = sceneNode.userData.originalOpacity
        canSetOpacity = false
      }

      catMap[category].layers.push({
        nodeId: layer.id,
        nodeName: layer.node_name,
        title: layer.display_name,
        visible,
        opacity,
        canSetOpacity,
        layerNumber: layer.layer,
        color: _getLayerColour(model.sceneNodes.get(layer.id), model.nodes),
      })

      catMap[category].allVisible &&= visible
      catMap[category].someVisible ||= visible
    } else {
      // Else get the layer's children
      const children: Layer[] =
        model.sceneNodes
          .get(layer.id)
          ?.children.map(c => {
            const node = model.nodes.get(c.userData.nodeId)
            if (!node) return

            // tree-traversed opacity uses Object3D.layers, quick algorithm uses Object3D.visible
            visible = !!(c.layers.isEnabled(LAYERS.DEFAULT) && c?.visible)
            let opacity = layer.opacity ?? 1
            let canSetOpacity = true
            if (!isNaN(c.userData.originalOpacity) && c.userData.originalOpacity !== 1) {
              opacity = c.userData.originalOpacity
              canSetOpacity = false
            }

            catMap[category].allVisible &&= visible
            catMap[category].someVisible ||= visible

            return {
              nodeId: node.id,
              nodeName: node.node_name,
              title: node.display_name,
              visible,
              opacity,
              canSetOpacity,
              layerNumber: node.layer,
              color: _getLayerColour(c, model.nodes),
            }
          })
          .filter((x): x is Layer => !!x)
          .sort((a, b) => a.layerNumber - b.layerNumber) ?? []

      catMap[category].layers.push(...children)
    }

    return catMap
  }, {} as Record<string, Omit<LayerGroup, 'name'>>)

  const layerGroups = Object.keys(categoryMap)
    .sort((a, b) => {
      if (a === DEFAULT_CATEGORY) {
        return 1
      }

      if (b === DEFAULT_CATEGORY) {
        return -1
      }

      return 0
    })
    .map(key => ({ name: key, ...categoryMap[key] }))

  layerGroups.reduce((acc: Layer[], group) => acc.concat(group.layers), []).forEach((val, idx) => (val.position = idx))

  return layerGroups
}
